// @ts-nocheck
import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { STAGE_ENV, DEV_ENV, LOCAL } from "@shared/common/constant";
import initSentry from "@shared/common/sentry";
import GlobalLoader from "./app/component/widget/skeleton/GlobalLoader";
import * as serviceWorker from "./serviceWorker";
import * as Sentry from "@sentry/react";

const MF_DIAG_PREFIX = "[MF-DIAG]";
const shouldLogDiagnostics =
    typeof window !== "undefined" &&
    ((window as any).__MF_DIAG_ENABLED__ === true ||
        window.location.hostname.includes("localhost") ||
        import.meta.env.VITE_MF_DEBUG === "true");

const bootstrapStartedAt = Date.now();
const logDiagnostics = (message, payload = {}) => {
    if (!shouldLogDiagnostics) return;
    const elapsedMs = Date.now() - bootstrapStartedAt;
    console.warn(MF_DIAG_PREFIX, message, { elapsedMs, ...payload });
};

const serializeError = (error) => {
    if (!error) return null;
    return {
        name: error.name,
        message: error.message,
        stack: error.stack
    };
};

if (shouldLogDiagnostics) {
    window.__MF_DIAG_ENTRY_STARTED__ = true;
    window.__MF_DIAG__ = {
        startedAt: bootstrapStartedAt,
        href: window.location.href,
        mode: import.meta.env.MODE,
        events: []
    };
    logDiagnostics("Shell entry module evaluated", {
        href: window.location.href,
        mode: import.meta.env.MODE,
        userAgent: navigator.userAgent
    });

    window.addEventListener("error", (event) => {
        logDiagnostics("window.error (from index.tsx)", {
            message: event?.message,
            filename: event?.filename,
            lineno: event?.lineno,
            colno: event?.colno,
            error: serializeError(event?.error)
        });
    });

    window.addEventListener("unhandledrejection", (event) => {
        logDiagnostics("unhandledrejection (from index.tsx)", {
            reasonType: typeof event?.reason,
            reason:
                event?.reason && typeof event.reason === "object" && "message" in event.reason
                    ? event.reason.message
                    : String(event?.reason),
            error: serializeError(event?.reason)
        });
    });
}

window.addEventListener(
    "error",
    (event) => {
        const target = event?.target as HTMLScriptElement | null;
        if (target?.tagName === "SCRIPT" && target?.src) {
            Sentry.captureMessage("script-load-error", {
                level: "error",
                tags: { area: "module-federation" },
                extra: { src: target.src, page: location.href, userAgent: navigator.userAgent }
            });
        }
    },
    true
);

// Ensure a minimal process shim for libraries expecting Node process
if (typeof window !== "undefined") {
    logDiagnostics("Applying process shim");
    const p = window.process || {};
    if (typeof p.cwd !== "function") {
        p.cwd = () => "/";
    }
    p.env = p.env || import.meta.env;
    window.process = p;
}

// Guard against forbidden request headers some SDKs attempt to set (e.g., react-contentful)
if (typeof window !== "undefined" && window.XMLHttpRequest) {
    logDiagnostics("Installing XMLHttpRequest header guard");
    const forbidden = new Set(["user-agent", "User-Agent", "accept-encoding", "Accept-Encoding"]);
    const origSetRequestHeader = window.XMLHttpRequest.prototype.setRequestHeader;
    window.XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
        if (forbidden.has(name)) {
            return;
        }
        return origSetRequestHeader.call(this, name, value);
    };
}

// Strip browser-forbidden headers some SDKs try to set
axios.interceptors.request.use((config) => {
    logDiagnostics("axios request interceptor hit", {
        url: config?.url,
        method: config?.method
    });
    if (config?.headers) {
        delete config.headers["user-agent"];
        delete config.headers["User-Agent"];
        delete config.headers["accept-encoding"];
        delete config.headers["Accept-Encoding"];
    }
    return config;
});

// Stylis plugin to replace :first-child with :first-of-type (avoids SSR warning)
const firstChildToFirstOfType = (context, _content, selectors) => {
    if (context === 2 && Array.isArray(selectors)) {
        for (let i = 0; i < selectors.length; i += 1) {
            if (typeof selectors[i] === "string" && selectors[i].includes(":first-child")) {
                selectors[i] = selectors[i].replace(/:first-child/g, ":first-of-type");
            }
        }
    }
};

const emotionCache = createCache({
    key: "css",
    prepend: true,
    stylisPlugins: [firstChildToFirstOfType]
});
logDiagnostics("Emotion cache created", { key: "css" });
const App = lazy(() => {
    logDiagnostics("Starting lazy import for app/app.jsx");
    return import("./app/app.jsx")
        .then((module) => {
            logDiagnostics("Lazy import resolved for app/app.jsx", {
                hasDefault: !!module?.default
            });
            return module;
        })
        .catch((error) => {
            logDiagnostics("Lazy import failed for app/app.jsx", {
                error: serializeError(error)
            });
            throw error;
        });
});

if (!location.host.includes(LOCAL)) {
    logDiagnostics("Initializing Sentry", { host: location.host });
    initSentry();
}

const root = document.getElementById("root");
logDiagnostics("Root lookup complete", {
    rootExists: !!root,
    rootTagName: root?.tagName || null
});
if (root) {
    logDiagnostics("Calling ReactDOM.render");
    ReactDOM.render(
        <CacheProvider value={emotionCache}>
            <div>
                <Helmet>
                    {(location.host.includes(STAGE_ENV) || location.host.includes(DEV_ENV)) && (
                        <meta name="robots" content="noindex" />
                    )}
                    <title>{"Hapimag"}</title>
                    <meta name="description" content="Hapimag-SPA" />
                    <meta name="seobility" content={import.meta.env.VITE_SEOBILITY_KEY} />
                    <script
                        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
                        type="text/javascript"
                        data-domain-script={import.meta.env.VITE_ONETRUST_SCRIPT_ID}
                    />
                    <script type="text/javascript">{"function OptanonWrapper() { }"}</script>
                </Helmet>

                {/* @ts-ignore */}
                <Suspense fallback={<GlobalLoader />}>
                    {/* @ts-ignore */}
                    <App />
                </Suspense>
            </div>
        </CacheProvider>,
        root
    );
    logDiagnostics("ReactDOM.render returned");
} else {
    logDiagnostics("Root element missing; render skipped");
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
logDiagnostics("serviceWorker.unregister called");
